import { useTranslation } from 'next-i18next';
import React, { useContext } from 'react';

import classNames from 'classnames';
import Image from 'next/image';
import PropTypes from 'prop-types';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import { trackContactLead } from '../../utils/dataLayer';
import ButtonPrimary from '../ButtonPrimary';
import ButtonSecondary from '../ButtonSecondary';
import PhotoCred from '../PhotoCred';
import styles from './CampaignHero.module.scss';
import useWindowWidthTitles from '../../hooks/useWindowWidthTitles';
import { GradientContext } from '../../contexts/PageContexts';

const CampaignHero = ({
    label = '',
    titleTop = '',
    titleBottom = '',
    text = '',
    image,
    richtext = true,
    lastUpdated,
    share,
    isDesktop,
    eventDateStart = '',
    eventDateEnd = '',
    eventLocation = '',
}) => {
    const gradient = useContext(GradientContext);
    const [ref, isVisible] = useIntersectionObserver();
    const [imageRef, imageIsVisible] = useIntersectionObserver();
    const imageGridClasses = classNames('grid', {
        [styles['ImageGrid']]: true,
        [styles['ImageVisible']]: imageIsVisible,
    });

    const width = useWindowWidthTitles(isDesktop);

    const isMobile = width < 768;

    const titleContainerClasses = classNames(styles['TitleContainer'], {
        [styles['RichText']]: richtext,
    });

    const textClasses = classNames(styles['Text'], { [styles['RichText']]: richtext });
    const classes = classNames(
        styles['Root'],
        {
            [styles['Visible']]: isVisible,
            [styles['IsFromRichText']]: richtext,
            [styles['HasImage']]: !!image,
        },
        styles[gradient]
    );

    const buttonContainerClasses = classNames(styles['ButtonContainer'], {
        [styles['ButtonContainerRichText']]: richtext,
    });
    const imageStyle = {
        objectFit: 'cover',
    };

    if (!!image && !!image.focal && !!image.focal.x && !!image.focal.y) {
        imageStyle.objectPosition = `${image.focal.x} ${image.focal.y}`;
    }

    let eventDate = '';
    if (eventDateStart && eventDateEnd) {
        eventDate = `${eventDateStart} - ${eventDateEnd}`;
    } else if (eventDateStart) {
        eventDate = eventDateStart;
    }

    const { t } = useTranslation();
    return (
        <section className={classes} ref={ref}>
            <div className={styles['Blob']} />
            <div ref={imageRef} className={imageGridClasses}>
                {!!image?.url && (
                    <div className={`${styles['ImageContainer']}`}>
                        <Image
                            className={styles['Image']}
                            src={image.url}
                            alt={image?.alt}
                            title={image?.title}
                            fill
                            style={imageStyle}
                        />
                        <PhotoCred credit={image?.credit} />
                    </div>
                )}
            </div>

            <div className={`${styles['Content']} grid`}>
                {isMobile ? (
                    <h1 className={`${styles['Title']} ${styles['TitleMobile']} grid`}>
                        <span className={titleContainerClasses}>
                            <div>
                                <div className={styles['Label']}>{label}</div>
                                <span className={styles['TitleTop']}>
                                    {titleTop}
                                    <span className={styles['TitleBottom']}> {titleBottom}</span>
                                </span>
                            </div>
                        </span>
                    </h1>
                ) : (
                    <h1 className={`${styles['Title']} ${styles['TitleDesktop']} grid`}>
                        <span className={titleContainerClasses}>
                            <div>
                                <span className={styles['Label']}>{label}</span>
                                <span className={styles['TitleTop']}>
                                    {titleTop}
                                    <span className={styles['TitleBottom']}> {titleBottom}</span>
                                </span>
                            </div>
                        </span>
                    </h1>
                )}

                <div className={`grid`}>
                    <p className={textClasses}>{text}</p>
                    {(eventDate || eventLocation) && (
                        <div className={styles['MetaInfo']}>
                            {eventDate && (
                                <div className={styles['DateContainer']}>
                                    <div className={styles['DateIcon']}>
                                        <Image width={24} height={24} src={`/img/icon-date-white.svg`} alt="" />
                                    </div>
                                    <span className={styles['DateText']}>{eventDate}</span>
                                </div>
                            )}

                            {eventLocation && (
                                <div className={styles['LocationContainer']}>
                                    <div className={styles['LocationIcon']}>
                                        <Image width={24} height={24} src={`/img/icon-pin-white.svg`} alt="" />
                                    </div>
                                    <span className={styles['LocationText']}>{eventLocation}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

CampaignHero.propTypes = {
    titleTop: PropTypes.string,
    titleBottom: PropTypes.string,
    text: PropTypes.string,
};

export default CampaignHero;
